import { useState } from 'react';
import type { ActivityWindow } from '@bbbk/schema';
import { VBX, VBY, ChartTooltip, ChartYAxis,
  ChartFrame,
  CATEGORY_BAR_SLOT_INSET,
  ChartNote,
} from '../bookings/chartParts';
import { countAxis, type SectionTotal } from './accountCharts';
import { WINDOW_LABEL, WINDOW_EMPTY_PHRASE } from './activityFormat';

// Page views by section — single-series vertical bars over the nav sections (the
// heatmap's columns, summed across the team). Same .chart-* chrome as the
// Bookings / Finances charts: a 0–100 viewBox SVG with HTML-overlaid axes, a
// brand fill, and the shared hover tooltip. One bar per section, x-labels
// centred under each.
//
// `window` is the window the DATA was queried over — the chart names it in the
// tooltip and the empty state rather than saying "this window", which told the
// reader nothing and let a bounded total read as an all-time one.
export function AccountSectionBars({ data, window }: { data: SectionTotal[]; window: ActivityWindow }) {
  const [hi, setHi] = useState<number | null>(null);
  const total = data.reduce((a, d) => a + d.value, 0);
  const { max: yMax, ticks: yTicks } = countAxis(Math.max(0, ...data.map((d) => d.value)));
  const yAt = (v: number) => VBY - (v / yMax) * VBY;
  const n = data.length;
  const slotW = VBX / Math.max(1, n);
  const inset = slotW * CATEGORY_BAR_SLOT_INSET;
  const w = Math.max(0.4, slotW - inset * 2);
  const hp = hi != null ? data[hi]! : null;
  const leftPct = hi != null ? ((hi + 0.5) / n) * 100 : 0;
  const anchorPct = hp ? Math.max(0, Math.min(100, (yAt(hp.value) / VBY) * 100)) : 0;

  // Empty renders INSIDE the canvas, not as a dashed panel: a card whose chart
  // is one height and whose empty state is another makes the page jump the
  // moment the window changes.
  if (total === 0)
    return (
      <ChartFrame className="account-chart">
        <ChartNote>No page views {WINDOW_EMPTY_PHRASE[window]}.</ChartNote>
      </ChartFrame>
    );

  return (
    <ChartFrame className="account-chart">
        <div className="chart-svg-wrap" onMouseLeave={() => setHi(null)}>
          <svg className="chart-svg" width="100%" height="100%" viewBox={`0 0 ${VBX} ${VBY}`} preserveAspectRatio="none" role="img" aria-label="Page views by section">
            {yTicks.map((v, i) => (
              <line key={'g' + i} x1="0" y1={yAt(v)} x2={VBX} y2={yAt(v)} stroke="var(--border)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            ))}
            {data.map((d, i) => (
              <rect
                key={d.key}
                x={i * slotW + inset}
                y={yAt(d.value)}
                width={w}
                height={Math.max(0, VBY - yAt(d.value))}
                fill="var(--brand)"
                style={{ opacity: hi != null && hi !== i ? 0.55 : 1 }}
                onMouseEnter={() => setHi(i)}
              />
            ))}
          </svg>
          {hp && (
            <ChartTooltip
              head={`${hp.label} · ${WINDOW_LABEL[window]}`}
              total={{ label: 'Page views', value: hp.value }}
              leftPct={leftPct}
              anchorPct={anchorPct}
            />
          )}
        </div>
        <ChartYAxis ticks={yTicks} />
        <div className="chart-x-axis cat">
          {data.map((d, i) => (
            <span key={d.key} className={'cat-lbl' + (hi === i ? ' on' : '')} style={{ left: `${((i + 0.5) / n) * 100}%` }} title={d.label}>
              {d.short}
            </span>
          ))}
      </div>
    </ChartFrame>
  );
}
